/**
 * A1 — Contactos del cliente: las personas con las que se habla (el administrador de
 * una empresa, el hijo de un cliente mayor…). Uno puede ser el interlocutor principal.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { AlertCircle, Loader2, Mail, Pencil, Phone, Plus, Star, Trash2, Users } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import type { ClientContactDto, ClientContactListResponse } from '@toga/shared'

const inputCls =
  'w-full bg-transparent border border-base rounded-lg px-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

interface Props {
  clientId: string
  canWrite: boolean
  /** Avisa a la ficha de que cambió el nº de contactos (insignia de la pestaña). */
  onChanged?: () => void
}

export default function ContactsPanel({ clientId, canWrite, onChanged }: Props) {
  const [items, setItems] = useState<ClientContactDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<ClientContactDto | 'new' | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await api.get<ClientContactListResponse>(`/clients/${clientId}/contacts`)
      setItems(r.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los contactos')
      setItems([])
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const afterChange = async () => {
    await load()
    onChanged?.()
  }

  const onDelete = async (c: ClientContactDto) => {
    if (!window.confirm(`¿Eliminar el contacto «${c.name}»? Se borra definitivamente.`)) return
    try {
      await api.delete(`/clients/${clientId}/contacts/${c.id}`)
      await afterChange()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar')
    }
  }

  const makePrimary = async (c: ClientContactDto) => {
    try {
      await api.patch(`/clients/${clientId}/contacts/${c.id}`, { isPrimary: true })
      await afterChange()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar el principal')
    }
  }

  return (
    <section className="surface p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <Users size={15} className="text-toga-300" />
          Contactos
          {items && <span className="t-faint font-normal">· {items.length}</span>}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
          >
            <Plus size={13} /> Añadir contacto
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {items === null ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">
          Sin contactos. Añade a las personas con las que trata el bufete: el administrador, un
          familiar, el responsable de facturación…
        </p>
      ) : (
        <ul className="divide-y divide-base/50">
          {items.map((c) => (
            <li key={c.id} className="py-2.5 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="t-primary text-sm font-medium flex items-center gap-2 flex-wrap">
                  {c.name}
                  {c.isPrimary && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                      <Star size={9} /> Principal
                    </span>
                  )}
                  {c.role && <span className="t-muted text-xs font-normal">· {c.role}</span>}
                </div>
                <div className="text-xs t-secondary mt-0.5 flex items-center gap-3 flex-wrap">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="hover:underline inline-flex items-center gap-1">
                      <Mail size={11} /> {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="hover:underline inline-flex items-center gap-1">
                      <Phone size={11} /> {c.phone}
                    </a>
                  )}
                </div>
                {c.notes && <div className="text-xs t-faint mt-1 line-clamp-2">{c.notes}</div>}
              </div>
              {canWrite && (
                <div className="flex items-center gap-1 shrink-0">
                  {!c.isPrimary && (
                    <button
                      type="button"
                      onClick={() => void makePrimary(c)}
                      title="Marcar como interlocutor principal"
                      className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-faint hover:text-amber-300"
                    >
                      <Star size={13} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    title="Editar contacto"
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-faint hover:text-toga-300"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete(c)}
                    title="Eliminar contacto"
                    className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-faint hover:text-rose-400"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <ContactModal
          clientId={clientId}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await afterChange()
          }}
        />
      )}
    </section>
  )
}

function ContactModal({
  clientId,
  initial,
  onClose,
  onSaved,
}: {
  clientId: string
  initial: ClientContactDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [role, setRole] = useState(initial?.role ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary ?? false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    // Vacío → null: la API distingue «sin valor» de «no tocar».
    const payload = {
      name: name.trim(),
      role: role.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      notes: notes.trim() || null,
      isPrimary,
    }
    try {
      if (initial) await api.patch(`/clients/${clientId}/contacts/${initial.id}`, payload)
      else await api.post(`/clients/${clientId}/contacts`, payload)
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'No se pudo guardar'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={initial ? 'Editar contacto' : 'Nuevo contacto'}>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted">Nombre *</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${inputCls} mt-0.5`}
            required
            minLength={2}
            autoFocus
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Cargo o relación</span>
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Administrador, hija, gestoría…"
              className={`${inputCls} mt-0.5`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Teléfono</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`${inputCls} mt-0.5`}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted">Correo electrónico</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputCls} mt-0.5`}
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Notas</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={`${inputCls} mt-0.5`}
          />
        </label>
        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="accent-toga-500"
          />
          Interlocutor principal
        </label>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || name.trim().length < 2}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  )
}
